/**
 * Licensed to the Apache Software Foundation (ASF) under one
 * or more contributor license agreements.  See the NOTICE file
 * distributed with this work for additional information
 * regarding copyright ownership.  The ASF licenses this file
 * to you under the Apache License, Version 2.0 (the
 * "License"); you may not use this file except in compliance
 * with the License.  You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */
import rison from "rison";
import { SupersetClient, NO_TIME_RANGE, JsonObject } from "@superset-ui/core";
import { getClientErrorObject } from "src/utils/getClientErrorObject";
import { useSelector } from "react-redux";
import { COMMON_RANGE_VALUES_SET, CALENDAR_RANGE_VALUES_SET, customTimeRangeDecode } from ".";
import { FrameType } from "../types";

export const SEPARATOR = " : ";

export const buildTimeRangeString = (since: string, until: string): string => `${since}${SEPARATOR}${until}`;

const formatDateEndpoint = (dttm: string, isStart?: boolean): string =>
    dttm.replace("T00:00:00", "") || (isStart ? "-∞" : "∞");

export const formatTimeRange = (timeRange: string, columnPlaceholder = "col") => {
    const splitDateRange = timeRange.split(SEPARATOR);
    if (splitDateRange.length === 1) return timeRange;
    return `${formatDateEndpoint(splitDateRange[0], true)} ≤ ${columnPlaceholder} < ${formatDateEndpoint(
        splitDateRange[1]
    )}`;
};

export const guessFrame = (timeRange: string): FrameType => {
    if (COMMON_RANGE_VALUES_SET.has(timeRange)) {
        return "Common";
    }
    if (CALENDAR_RANGE_VALUES_SET.has(timeRange)) {
        return "Calendar";
    }
    if (timeRange === NO_TIME_RANGE) {
        return "No filter";
    }
    if (customTimeRangeDecode(timeRange).matchedFlag) {
        return "Custom";
    }
    return "Advanced";
};

export const fetchTimeRange = async (timeRange: string, columnPlaceholder = "col") => {
    const query = rison.encode_uri(timeRange);
    const endpoint = `/api/v1/time_range/?q=${query}`;
    try {
        const response = await SupersetClient.get({ endpoint });
        const timeRangeString = buildTimeRangeString(
            response?.json?.result?.since || "",
            response?.json?.result?.until || ""
        );
        return {
            value: formatTimeRange(timeRangeString, columnPlaceholder),
        };
    } catch (response) {
        const clientError = await getClientErrorObject(response);
        return {
            error: clientError.message || clientError.error || response.statusText,
        };
    }
};

export function useDefaultTimeFilter() {
    return useSelector((state: JsonObject) => state?.common?.conf?.DEFAULT_TIME_FILTER) ?? NO_TIME_RANGE;
}
